import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ProductCard, { discountOf } from '../components/ProductCard.jsx';
import { useStore } from '../context/StoreContext.jsx';
import { slug, sizeRank } from '../config/brand.js';

const TITLES = {
  clothing: 'Clothing',
  handbags: 'Handbags',
  'new-arrivals': 'New Arrivals',
  'best-sellers': 'Best Sellers',
  sale: 'Sale',
};
const PAGE = 24;
const newest = (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''));

export default function Shop() {
  const { main, sub } = useParams();
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();
  const { products, settings } = useStore();
  const [sort, setSort] = useState('newest');
  const [size, setSize] = useState('');
  const [inStock, setInStock] = useState(false);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => { setShown(PAGE); setSize(''); }, [main, sub, q]);

  // Products for this section, before filters
  const base = useMemo(() => {
    let list = products;
    if (main === 'clothing' || main === 'handbags') list = list.filter((p) => p.mainCategory === main);
    else if (main === 'new-arrivals') {
      const flagged = list.filter((p) => p.newArrival);
      list = flagged.length ? flagged : [...list].sort(newest).slice(0, 12);
    } else if (main === 'best-sellers') list = list.filter((p) => p.bestSeller);
    else if (main === 'sale') list = list.filter((p) => discountOf(p) > 0);
    if (sub) list = list.filter((p) => slug(p.category) === sub);
    if (q) list = list.filter((p) => [p.name, p.description, p.color, p.category, p.sku].join(' ').toLowerCase().includes(q));
    return list;
  }, [products, main, sub, q]);

  const sizes = useMemo(() => {
    const set = new Set();
    base.forEach((p) => (p.sizes || []).forEach((s) => s.stock > 0 && set.add(s.size)));
    return [...set].sort((a, b) => sizeRank(a) - sizeRank(b));
  }, [base]);

  const list = useMemo(() => {
    let l = base.filter((p) => !inStock || p.stock > 0);
    if (size) l = l.filter((p) => (p.sizes || []).some((s) => s.size === size && s.stock > 0));
    const sorters = {
      newest, 'price-asc': (a, b) => a.price - b.price, 'price-desc': (a, b) => b.price - a.price,
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...l].sort(sorters[sort] || newest);
  }, [base, inStock, size, sort]);

  // Sub category chips
  const cats = settings.categories || {};
  const chips = (main === 'clothing' || main === 'handbags')
    ? cats[main] || []
    : [...new Set([...(cats.clothing || []), ...(cats.handbags || [])])];
  const basePath = `/shop/${main || ''}`.replace(/\/$/, '');
  const title = q ? `Search results for "${params.get('q')}"` : TITLES[main] || 'All Products';

  return (
    <div className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / <span>{TITLES[main] || 'Shop'}</span>
      </nav>
      <div className="page__head">
        <h1 className="page__title">{title} <small>{list.length} {list.length === 1 ? 'product' : 'products'}</small></h1>
        <button type="button" className="linkbtn page__filter-toggle" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide filters' : 'Filter and sort'}
        </button>
      </div>

      {!q && chips.length > 0 && (main === 'clothing' || main === 'handbags') && (
        <div className="chips">
          <Link to={basePath} className={`chip${!sub ? ' is-active' : ''}`}>All</Link>
          {chips.map((c) => (
            <Link key={c} to={`${basePath}/${slug(c)}`} className={`chip${sub === slug(c) ? ' is-active' : ''}`}>{c}</Link>
          ))}
        </div>
      )}

      <div className={`filters${open ? ' is-open' : ''}`}>
        <label>Sort
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
            <option value="name">Name</option>
          </select>
        </label>
        {sizes.length > 0 && (
          <div className="filters__sizes" role="group" aria-label="Size">
            <span>Size</span>
            <button type="button" className={`sizebtn${!size ? ' is-active' : ''}`} onClick={() => setSize('')}>All</button>
            {sizes.map((s) => (
              <button key={s} type="button" className={`sizebtn${size === s ? ' is-active' : ''}`} onClick={() => setSize(s)}>{s}</button>
            ))}
          </div>
        )}
        <label className="check"><input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> In stock only</label>
      </div>

      {list.length ? (
        <>
          <div className="grid">{list.slice(0, shown).map((p) => <ProductCard key={p.id} product={p} />)}</div>
          <p className="muted center">Showing {Math.min(shown, list.length)} of {list.length} products</p>
          {shown < list.length && <div className="center"><button type="button" className="btn" onClick={() => setShown((n) => n + PAGE)}>Load more</button></div>}
        </>
      ) : (
        <div className="soon">
          <p className="eyebrow">{q ? 'No matches' : 'Coming soon'}</p>
          <p className="soon__text">{q ? 'Try a different search term.' : 'This collection is on its way. Please check back shortly.'}</p>
        </div>
      )}
    </div>
  );
}
